import React, { useCallback, useEffect, useState } from "react";
import {
  Typography, Button, TextField, Switch, FormControlLabel,
  Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, CircularProgress, Chip, Box,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// feat_210 Fase D — editor superadmin de BulkSendProfiles. Solo español (mismo
// patrón que SuperAdminMatchProp/SuperAdminAI: superadmin es interno, casi no
// usa i18n.t, la mayoría del texto va hardcodeado directo en JSX).

const REQUIRED_KEYS = ["fileConfig", "rowFilters", "normalization", "notesJson", "templateVars", "postSend"];

const emptyDraft = () => ({
  name: "",
  description: "",
  active: true,
  jsonText: JSON.stringify(
    { fileConfig: {}, rowFilters: [], normalization: {}, notesJson: {}, templateVars: {}, postSend: {} },
    null, 2
  ),
});

const profileToDraft = (p) => ({
  id: p.id,
  name: p.name || "",
  description: p.description || "",
  active: !!p.active,
  jsonText: JSON.stringify(
    {
      fileConfig: p.fileConfig ?? {},
      rowFilters: p.rowFilters ?? [],
      normalization: p.normalization ?? {},
      notesJson: p.notesJson ?? {},
      templateVars: p.templateVars ?? {},
      postSend: p.postSend ?? {},
    },
    null, 2
  ),
});

// Validación MÍNIMA en cliente: JSON parseable + las 6 keys EXISTEN (aunque
// sea con objetos vacíos). El backend no valida la semántica profunda — de
// eso se encarga el ExcelMappingEngine al parsear un archivo real.
const validateJson = (text) => {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { error: `JSON inválido: ${e.message}` };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { error: "El JSON debe ser un objeto." };
  }
  const missing = REQUIRED_KEYS.filter((k) => !(k in parsed));
  if (missing.length > 0) {
    return { error: `Faltan las keys: ${missing.join(", ")} (pueden ir vacías: {} o []).` };
  }
  return { parsed };
};

const BulkSendProfilesEditor = ({ companyId }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null); // null = list view; object = editor view
  const [jsonError, setJsonError] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchProfiles = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/companies/${companyId}/bulk-send-profiles`);
      setProfiles(Array.isArray(data) ? data : []);
    } catch (e) {
      toastError(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const openNew = () => { setJsonError(""); setDraft(emptyDraft()); };
  const openEdit = (p) => { setJsonError(""); setDraft(profileToDraft(p)); };
  const closeEditor = () => { setDraft(null); setJsonError(""); };

  const handleSave = async () => {
    if (!draft.name || draft.name.trim() === "") {
      toast.error("El nombre es obligatorio");
      return;
    }
    const { error, parsed } = validateJson(draft.jsonText);
    if (error) {
      setJsonError(error);
      return;
    }
    setJsonError("");
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        description: draft.description,
        active: draft.active,
        fileConfig: parsed.fileConfig,
        rowFilters: parsed.rowFilters,
        normalization: parsed.normalization,
        notesJson: parsed.notesJson,
        templateVars: parsed.templateVars,
        postSend: parsed.postSend,
      };
      if (draft.id) {
        await api.put(`/superadmin/companies/${companyId}/bulk-send-profiles/${draft.id}`, body);
        toast.success("Profile actualizado");
      } else {
        await api.post(`/superadmin/companies/${companyId}/bulk-send-profiles`, body);
        toast.success("Profile creado");
      }
      closeEditor();
      fetchProfiles();
    } catch (e) {
      // Mismo patrón del fix de dedupe: leer el error del catch de axios,
      // nunca asumir que la respuesta llega por el path de éxito.
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  // Switch de la lista: ON->OFF = soft-delete (DELETE, backend hace active=false).
  // OFF->ON = reactivar (PUT parcial {active:true}).
  const handleToggleActive = async (p) => {
    try {
      if (p.active) {
        await api.delete(`/superadmin/companies/${companyId}/bulk-send-profiles/${p.id}`);
        toast.success(`"${p.name}" desactivado`);
      } else {
        await api.put(`/superadmin/companies/${companyId}/bulk-send-profiles/${p.id}`, { active: true });
        toast.success(`"${p.name}" activado`);
      }
      fetchProfiles();
    } catch (e) {
      toastError(e);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" padding={4}><CircularProgress /></Box>;
  }

  if (draft) {
    return (
      <div style={{ paddingTop: 16 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>
          {draft.id ? `Editar profile #${draft.id}` : "Nuevo profile"}
        </Typography>
        <TextField
          label="Nombre" fullWidth margin="dense" size="small"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <TextField
          label="Descripción" fullWidth margin="dense" size="small"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
        <FormControlLabel
          control={
            <Switch
              color="primary" size="small" checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
          }
          label={<Typography variant="caption">Activo</Typography>}
          style={{ marginTop: 4 }}
        />
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12, marginBottom: 4 }}>
          Config JSON (fileConfig, rowFilters, normalization, notesJson, templateVars, postSend combinados en un solo objeto — mismo formato que reciben los endpoints):
        </Typography>
        <TextField
          fullWidth multiline minRows={16} maxRows={26}
          variant="outlined" size="small"
          value={draft.jsonText}
          onChange={(e) => { setDraft({ ...draft, jsonText: e.target.value }); if (jsonError) setJsonError(""); }}
          error={!!jsonError}
          helperText={jsonError || " "}
          InputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
        />
        <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={closeEditor} disabled={saving}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 16 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          Perfiles de envío masivo ({profiles.length})
        </Typography>
        <Button size="small" variant="outlined" color="primary" startIcon={<AddIcon />} onClick={openNew}>
          Nuevo profile
        </Button>
      </Box>
      {profiles.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: "center" }}>
          No hay perfiles creados para esta empresa todavía.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell align="center">Activo</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.name}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">{p.description || "—"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">
                    {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "—"}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" color="primary" checked={!!p.active} onChange={() => handleToggleActive(p)} />
                  {!p.active && <Chip size="small" label="inactivo" style={{ marginLeft: 4 }} />}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default BulkSendProfilesEditor;
